import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API, BRAND } from "../App";
import {
  LogOut, Plus, Edit, Trash2, Car, Users, X, Save, Phone, Mail, Image, UserPlus,
  Truck, FileText, CheckCircle, Clock, DollarSign, MessageSquare, LayoutDashboard,
  Star, StarOff, ChevronRight
} from "lucide-react";

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [data, setData] = useState({ cars: [], inquiries: [], transports: [], soldImages: [], subscribers: [], contacts: [] });
  const [loading, setLoading] = useState(true);
  const [showCarModal, setShowCarModal] = useState(false);
  const [showSoldModal, setShowSoldModal] = useState(false);
  const [showTransportModal, setShowTransportModal] = useState(false);
  const [editingCar, setEditingCar] = useState(null);
  const [selectedTransport, setSelectedTransport] = useState(null);
  const [newSoldUrl, setNewSoldUrl] = useState("");
  const [carForm, setCarForm] = useState({
    make: "", model: "", year: new Date().getFullYear(), price: "", mileage: "",
    trim: "", features: "", description: "", vin: "",
    image_url: "", images: [], status: "available", featured: false
  });
  const [newImageUrl, setNewImageUrl] = useState("");
  const [transportUpdate, setTransportUpdate] = useState({ status: "", quote_amount: "", admin_notes: "" });

  const token = localStorage.getItem("token");
  const authHeaders = { Authorization: `Bearer ${token}` };
  const authConfig = { headers: authHeaders, withCredentials: true };

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [c, i, t, s, sub, co] = await Promise.all([
        axios.get(`${API}/cars`),
        axios.get(`${API}/inquiries`, authConfig),
        axios.get(`${API}/transport`, authConfig),
        axios.get(`${API}/sold-images`),
        axios.get(`${API}/subscribers`, authConfig),
        axios.get(`${API}/contact`, authConfig),
      ]);
      setData({ cars: c.data, inquiries: i.data, transports: t.data, soldImages: s.data, subscribers: sub.data, contacts: co.data });
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => { await logout(); navigate("/admin/login"); };

  // Car functions
  const openCarModal = (car = null) => {
    if (car) {
      setEditingCar(car);
      setCarForm({ ...car, images: car.images || [] });
    } else {
      setEditingCar(null);
      setCarForm({
        make: "", model: "", year: new Date().getFullYear(), price: "", mileage: "",
        trim: "", features: "", description: "", vin: "",
        image_url: "", images: [], status: "available", featured: false
      });
    }
    setNewImageUrl("");
    setShowCarModal(true);
  };

  const handleCarChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCarForm({ ...carForm, [name]: type === "checkbox" ? checked : (name === "year" || name === "price" || name === "mileage" ? Number(value) : value) });
  };

  const addImageToCar = () => {
    if (!newImageUrl.trim()) return;
    const updated = [...carForm.images, newImageUrl.trim()];
    setCarForm({ ...carForm, images: updated, image_url: carForm.image_url || newImageUrl.trim() });
    setNewImageUrl("");
  };

  const removeImageFromCar = (idx) => {
    const updated = carForm.images.filter((_, i) => i !== idx);
    setCarForm({ ...carForm, images: updated, image_url: updated[0] || "" });
  };

  const handleSaveCar = async (e) => {
    e.preventDefault();
    try {
      if (editingCar) {
        await axios.put(`${API}/cars/${editingCar.id}`, carForm, authConfig);
        toast.success("Car updated!");
      } else {
        await axios.post(`${API}/cars`, carForm, authConfig);
        toast.success("Car added!");
      }
      setShowCarModal(false);
      fetchAll();
    } catch (error) {
      toast.error("Failed to save car");
    }
  };

  const handleDeleteCar = async (id) => {
    if (!window.confirm("Delete this car?")) return;
    try {
      await axios.delete(`${API}/cars/${id}`, authConfig);
      toast.success("Car deleted!");
      fetchAll();
    } catch (e) { toast.error("Failed to delete"); }
  };

  // Sold image functions
  const handleAddSold = async (e) => {
    e.preventDefault();
    if (!newSoldUrl.trim()) return;
    try {
      await axios.post(`${API}/sold-images`, { image_url: newSoldUrl }, authConfig);
      toast.success("Photo added!");
      setNewSoldUrl("");
      setShowSoldModal(false);
      fetchAll();
    } catch (e) { toast.error("Failed to add"); }
  };

  const handleDeleteSold = async (id) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await axios.delete(`${API}/sold-images/${id}`, authConfig);
      toast.success("Deleted");
      fetchAll();
    } catch (e) { toast.error("Failed"); }
  };

  // Transport functions
  const openTransportModal = (t) => {
    setSelectedTransport(t);
    setTransportUpdate({ status: t.status, quote_amount: t.quote_amount || "", admin_notes: t.admin_notes || "" });
    setShowTransportModal(true);
  };

  const handleUpdateTransport = async (e) => {
    e.preventDefault();
    try {
      const payload = { 
        status: transportUpdate.status,
        admin_notes: transportUpdate.admin_notes,
      };
      if (transportUpdate.quote_amount) payload.quote_amount = parseFloat(transportUpdate.quote_amount);
      await axios.put(`${API}/transport/${selectedTransport.id}`, payload, authConfig);
      toast.success("Updated!");
      setShowTransportModal(false);
      fetchAll();
    } catch (e) { toast.error("Failed"); }
  };

  const handleDeleteTransport = async (id) => {
    if (!window.confirm("Delete this transport request?")) return;
    try {
      await axios.delete(`${API}/transport/${id}`, authConfig);
      toast.success("Deleted");
      fetchAll();
    } catch (e) { toast.error("Failed"); }
  };

  const handleDeleteInquiry = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    try {
      await axios.delete(`${API}/inquiries/${id}`, authConfig);
      toast.success("Deleted");
      fetchAll();
    } catch (e) { toast.error("Failed"); }
  };

  const handleDeleteContact = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await axios.delete(`${API}/contact/${id}`, authConfig);
      toast.success("Deleted");
      fetchAll();
    } catch (e) { toast.error("Failed"); }
  };

  const handleDeleteSub = async (id) => {
    if (!window.confirm("Remove this subscriber?")) return;
    try {
      await axios.delete(`${API}/subscribers/${id}`, authConfig);
      toast.success("Removed");
      fetchAll();
    } catch (e) { toast.error("Failed"); }
  };

  const formatPrice = (p) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(p);
  const formatDate = (d) => new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

  const transportStatusColors = {
    new: "bg-blue-100 text-blue-700",
    quoted: "bg-purple-100 text-purple-700",
    awaiting_approval: "bg-yellow-100 text-yellow-700",
    confirmed: "bg-green-100 text-green-700",
    in_progress: "bg-orange-100 text-orange-700",
    completed: "bg-gray-100 text-gray-700",
    cancelled: "bg-red-100 text-red-700",
  };

  const newTransportsCount = data.transports.filter(t => t.status === "new").length;
  const newInquiriesCount = data.inquiries.filter(i => i.status === "new").length;
  const newContactsCount = data.contacts.filter(c => c.status === "new").length;

  const tabs = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { id: "transport", label: "Transport Requests", icon: Truck, badge: newTransportsCount },
    { id: "inventory", label: "Vehicle Inventory", icon: Car },
    { id: "inquiries", label: "Vehicle Inquiries", icon: MessageSquare, badge: newInquiriesCount },
    { id: "contacts", label: "Contact Messages", icon: Mail, badge: newContactsCount },
    { id: "sold", label: "Sold Gallery", icon: Image },
    { id: "subscribers", label: "Subscribers", icon: UserPlus },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col lg:flex-row">
      {/* Sidebar */}
      <aside className="lg:w-64 bg-black text-white lg:min-h-screen">
        <div className="p-6 border-b border-white/10">
          <Link to="/" className="block">
            <img src={BRAND.logo} alt={BRAND.name} className="h-10 w-auto brightness-110" />
          </Link>
          <p className="text-xs text-white/60 mt-2">Admin Dashboard</p>
        </div>

        <nav className="p-4 space-y-1 lg:block flex overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors text-sm whitespace-nowrap ${activeTab === tab.id ? "bg-[#E31E24] text-white" : "text-white/70 hover:bg-white/10"}`}
            >
              <tab.icon size={18} />
              <span className="font-semibold">{tab.label}</span>
              {tab.badge > 0 && (
                <span className="ml-auto bg-white text-black text-xs px-2 py-0.5 font-bold">{tab.badge}</span>
              )}
            </button>
          ))}
        </nav>

        <div className="p-4 lg:absolute lg:bottom-0 lg:w-64 border-t border-white/10">
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 text-white/70 hover:text-white hover:bg-white/10 text-sm">
            <LogOut size={18} /> Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-x-hidden">
        <header className="bg-white border-b border-black/10 p-6">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-black uppercase">
              {tabs.find(t => t.id === activeTab)?.label}
            </h1>
            <span className="text-sm text-black/60">{user?.email}</span>
          </div>
        </header>

        <div className="p-6">
          {loading ? (
            <div className="flex justify-center py-20"><div className="spinner"></div></div>
          ) : (
            <>
              {/* DASHBOARD */}
              {activeTab === "dashboard" && (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                      { label: "New Transport", value: newTransportsCount, color: "bg-[#E31E24]", icon: Truck },
                      { label: "Vehicles", value: data.cars.length, color: "bg-black", icon: Car },
                      { label: "Inquiries", value: newInquiriesCount, color: "bg-blue-600", icon: MessageSquare },
                      { label: "Subscribers", value: data.subscribers.length, color: "bg-green-600", icon: UserPlus },
                    ].map((s, i) => (
                      <div key={i} className="bg-white p-6 border border-black/10">
                        <div className={`w-10 h-10 ${s.color} flex items-center justify-center mb-3`}>
                          <s.icon size={20} className="text-white" />
                        </div>
                        <p className="text-3xl font-black">{s.value}</p>
                        <p className="text-sm text-black/60 uppercase tracking-wide">{s.label}</p>
                      </div>
                    ))}
                  </div>

                  <div className="grid lg:grid-cols-2 gap-6">
                    <div className="bg-white border border-black/10 p-6">
                      <h3 className="font-black uppercase mb-4">Recent Transport Requests</h3>
                      {data.transports.slice(0, 5).length === 0 ? (
                        <p className="text-black/40 text-sm">No transport requests yet.</p>
                      ) : (
                        <div className="space-y-3">
                          {data.transports.slice(0, 5).map(t => (
                            <div key={t.id} onClick={() => { setActiveTab("transport"); openTransportModal(t); }}
                              className="flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 cursor-pointer">
                              <div>
                                <p className="font-bold text-sm">{t.full_name}</p>
                                <p className="text-xs text-black/60">{t.vehicle_year} {t.vehicle_make} {t.vehicle_model}</p>
                              </div>
                              <span className={`text-xs px-2 py-1 font-bold uppercase ${transportStatusColors[t.status] || ""}`}>{t.status}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="bg-white border border-black/10 p-6">
                      <h3 className="font-black uppercase mb-4">Quick Actions</h3>
                      <div className="grid grid-cols-2 gap-3">
                        <button onClick={() => openCarModal()} className="bg-[#E31E24] text-white font-bold uppercase py-3 text-sm hover:bg-[#C01A1F] flex items-center justify-center gap-2">
                          <Plus size={16} /> Add Vehicle
                        </button>
                        <button onClick={() => { setShowSoldModal(true); }} className="bg-black text-white font-bold uppercase py-3 text-sm hover:bg-black/80 flex items-center justify-center gap-2">
                          <Image size={16} /> Add Photo
                        </button>
                        <button onClick={() => setActiveTab("transport")} className="border border-black/20 font-bold uppercase py-3 text-sm hover:bg-black hover:text-white flex items-center justify-center gap-2">
                          <Truck size={16} /> Transport
                        </button>
                        <button onClick={() => setActiveTab("inquiries")} className="border border-black/20 font-bold uppercase py-3 text-sm hover:bg-black hover:text-white flex items-center justify-center gap-2">
                          <MessageSquare size={16} /> Inquiries
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* TRANSPORT */}
              {activeTab === "transport" && (
                <div className="space-y-4">
                  {data.transports.length === 0 ? (
                    <div className="bg-white p-20 text-center border border-black/10">
                      <Truck size={48} className="mx-auto text-black/20 mb-4" />
                      <p className="text-black/60">No transport requests yet.</p>
                    </div>
                  ) : (
                    data.transports.map(t => (
                      <div key={t.id} className="bg-white border border-black/10 p-6">
                        <div className="flex flex-wrap justify-between items-start gap-4 mb-4">
                          <div>
                            <h3 className="font-black text-lg">{t.full_name}</h3>
                            <div className="flex gap-4 mt-2 text-sm text-black/60">
                              <a href={`tel:${t.phone}`} className="flex items-center gap-1 hover:text-[#E31E24]"><Phone size={14} />{t.phone}</a>
                              <a href={`mailto:${t.email}`} className="flex items-center gap-1 hover:text-[#E31E24]"><Mail size={14} />{t.email}</a>
                            </div>
                          </div>
                          <span className={`text-xs px-3 py-1 font-black uppercase ${transportStatusColors[t.status] || ""}`}>{t.status.replace('_', ' ')}</span>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-3 mb-4">
                          <div className="bg-gray-50 p-3 border border-black/10">
                            <p className="text-xs uppercase text-black/40">Pickup</p>
                            <p className="font-bold text-sm">{t.pickup_address}</p>
                          </div>
                          <div className="bg-gray-50 p-3 border border-black/10">
                            <p className="text-xs uppercase text-black/40">Delivery</p>
                            <p className="font-bold text-sm">{t.delivery_address}</p>
                          </div>
                          <div className="bg-gray-50 p-3 border border-black/10">
                            <p className="text-xs uppercase text-black/40">Vehicle</p>
                            <p className="font-bold text-sm">{t.vehicle_year} {t.vehicle_make} {t.vehicle_model}</p>
                          </div>
                          <div className="bg-gray-50 p-3 border border-black/10">
                            <p className="text-xs uppercase text-black/40">Date / Time</p>
                            <p className="font-bold text-sm">{t.pickup_date || "N/A"} {t.pickup_time}</p>
                          </div>
                        </div>

                        <div className="flex gap-4 mb-4 text-sm">
                          <span>Runs: <strong>{t.runs_drives ? "Yes" : "No"}</strong></span>
                          <span>Rolls/Steers/Brakes: <strong>{t.rolls_steers_brakes ? "Yes" : "No"}</strong></span>
                          {t.quote_amount && <span>Quote: <strong className="text-[#E31E24]">{formatPrice(t.quote_amount)}</strong></span>}
                        </div>

                        {t.special_requirements && <p className="text-sm text-black/70 mb-2"><strong>Special:</strong> {t.special_requirements}</p>}
                        {t.additional_notes && <p className="text-sm text-black/70 mb-2"><strong>Notes:</strong> {t.additional_notes}</p>}
                        {t.admin_notes && <p className="text-sm text-black/70 mb-4 p-2 bg-yellow-50 border-l-2 border-yellow-500"><strong>Admin Notes:</strong> {t.admin_notes}</p>}

                        <div className="flex justify-between items-center pt-4 border-t border-black/10">
                          <p className="text-xs text-black/40">{formatDate(t.created_at)}</p>
                          <div className="flex gap-2">
                            <button onClick={() => openTransportModal(t)} className="bg-black text-white font-bold uppercase px-4 py-2 text-xs hover:bg-black/80">
                              Manage
                            </button>
                            <button onClick={() => handleDeleteTransport(t.id)} className="border border-red-300 text-red-500 font-bold uppercase px-4 py-2 text-xs hover:bg-red-50">
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* INVENTORY */}
              {activeTab === "inventory" && (
                <div>
                  <div className="flex justify-between mb-6">
                    <h2 className="text-xl font-black uppercase">Vehicle Inventory</h2>
                    <button onClick={() => openCarModal()} className="bg-[#E31E24] text-white font-bold uppercase px-4 py-2 hover:bg-[#C01A1F] flex items-center gap-2">
                      <Plus size={18} /> Add Vehicle
                    </button>
                  </div>

                  {data.cars.length === 0 ? (
                    <div className="bg-white p-20 text-center border border-black/10">
                      <Car size={48} className="mx-auto text-black/20 mb-4" />
                      <p className="text-black/60">No vehicles yet.</p>
                    </div>
                  ) : (
                    <div className="bg-white border border-black/10 overflow-x-auto">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>Vehicle</th><th>Price</th><th>Mileage</th><th>Status</th><th>Featured</th><th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.cars.map(car => (
                            <tr key={car.id}>
                              <td>
                                <div className="flex items-center gap-3">
                                  {(car.image_url || car.images?.[0]) ? (
                                    <img src={car.image_url || car.images[0]} className="w-16 h-10 object-cover" />
                                  ) : (
                                    <div className="w-16 h-10 bg-gray-200 flex items-center justify-center"><Car size={16} className="text-black/30" /></div>
                                  )}
                                  <div>
                                    <p className="font-bold">{car.year} {car.make} {car.model}</p>
                                    {car.trim && <p className="text-xs text-black/60">{car.trim}</p>}
                                  </div>
                                </div>
                              </td>
                              <td className="font-bold text-[#E31E24]">{formatPrice(car.price)}</td>
                              <td>{car.mileage.toLocaleString()} mi</td>
                              <td>
                                <span className={`px-2 py-1 text-xs font-bold uppercase ${car.status === "available" ? "bg-green-100 text-green-700" : car.status === "sold" ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-700"}`}>
                                  {car.status}
                                </span>
                              </td>
                              <td>{car.featured ? <Star size={16} className="text-[#E31E24] fill-[#E31E24]" /> : <StarOff size={16} className="text-black/20" />}</td>
                              <td>
                                <div className="flex gap-2">
                                  <button onClick={() => openCarModal(car)} className="p-2 text-black/60 hover:text-black"><Edit size={16} /></button>
                                  <button onClick={() => handleDeleteCar(car.id)} className="p-2 text-black/60 hover:text-red-500"><Trash2 size={16} /></button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* INQUIRIES */}
              {activeTab === "inquiries" && (
                <div className="space-y-4">
                  {data.inquiries.length === 0 ? (
                    <div className="bg-white p-20 text-center border border-black/10">
                      <MessageSquare size={48} className="mx-auto text-black/20 mb-4" />
                      <p className="text-black/60">No inquiries yet.</p>
                    </div>
                  ) : (
                    data.inquiries.map(inq => (
                      <div key={inq.id} className="bg-white border border-black/10 p-6">
                        <div className="flex justify-between mb-3">
                          <div>
                            <h3 className="font-black">{inq.name}</h3>
                            <div className="flex gap-4 mt-1 text-sm text-black/60">
                              <a href={`mailto:${inq.email}`} className="hover:text-[#E31E24]">{inq.email}</a>
                              <a href={`tel:${inq.phone}`} className="hover:text-[#E31E24]">{inq.phone}</a>
                            </div>
                          </div>
                          <button onClick={() => handleDeleteInquiry(inq.id)} className="text-black/40 hover:text-red-500"><Trash2 size={16} /></button>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3 mb-3 text-sm">
                          <p><strong>Budget:</strong> {inq.budget}</p>
                          <p><strong>Looking For:</strong> {inq.car_preference}</p>
                        </div>
                        {inq.message && <p className="text-sm text-black/70 mb-2">{inq.message}</p>}
                        <p className="text-xs text-black/40">{formatDate(inq.created_at)}</p>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* CONTACT MESSAGES */}
              {activeTab === "contacts" && (
                <div className="space-y-4">
                  {data.contacts.length === 0 ? (
                    <div className="bg-white p-20 text-center border border-black/10">
                      <Mail size={48} className="mx-auto text-black/20 mb-4" />
                      <p className="text-black/60">No messages yet.</p>
                    </div>
                  ) : (
                    data.contacts.map(c => (
                      <div key={c.id} className="bg-white border border-black/10 p-6">
                        <div className="flex justify-between mb-3">
                          <div>
                            <h3 className="font-black">{c.name}</h3>
                            {c.subject && <p className="text-sm text-black/80 font-semibold">{c.subject}</p>}
                            <div className="flex gap-4 mt-1 text-sm text-black/60">
                              <a href={`mailto:${c.email}`} className="hover:text-[#E31E24]">{c.email}</a>
                              {c.phone && <a href={`tel:${c.phone}`} className="hover:text-[#E31E24]">{c.phone}</a>}
                            </div>
                          </div>
                          <button onClick={() => handleDeleteContact(c.id)} className="text-black/40 hover:text-red-500"><Trash2 size={16} /></button>
                        </div>
                        <p className="text-sm text-black/70 mb-2 whitespace-pre-wrap">{c.message}</p>
                        <p className="text-xs text-black/40">{formatDate(c.created_at)}</p>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* SOLD GALLERY */}
              {activeTab === "sold" && (
                <div>
                  <div className="flex justify-between mb-6">
                    <h2 className="text-xl font-black uppercase">Sold Vehicle Photos</h2>
                    <button onClick={() => setShowSoldModal(true)} className="bg-[#E31E24] text-white font-bold uppercase px-4 py-2 hover:bg-[#C01A1F] flex items-center gap-2">
                      <Plus size={18} /> Add Photo
                    </button>
                  </div>

                  {data.soldImages.length === 0 ? (
                    <div className="bg-white p-20 text-center border border-black/10">
                      <Image size={48} className="mx-auto text-black/20 mb-4" />
                      <p className="text-black/60">No sold photos yet.</p>
                    </div>
                  ) : (
                    <div className="grid sm:grid-cols-3 lg:grid-cols-4 gap-4">
                      {data.soldImages.map(img => (
                        <div key={img.id} className="relative group">
                          <img src={img.image_url} alt="" className="w-full aspect-square object-cover" />
                          <button onClick={() => handleDeleteSold(img.id)} className="absolute top-2 right-2 bg-red-500 text-white p-2 opacity-0 group-hover:opacity-100">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* SUBSCRIBERS */}
              {activeTab === "subscribers" && (
                <div>
                  <h2 className="text-xl font-black uppercase mb-6">Email Subscribers</h2>
                  {data.subscribers.length === 0 ? (
                    <div className="bg-white p-20 text-center border border-black/10">
                      <UserPlus size={48} className="mx-auto text-black/20 mb-4" />
                      <p className="text-black/60">No subscribers yet.</p>
                    </div>
                  ) : (
                    <div className="bg-white border border-black/10 overflow-x-auto">
                      <table className="admin-table">
                        <thead><tr><th>Name</th><th>Email</th><th>Subscribed</th><th>Actions</th></tr></thead>
                        <tbody>
                          {data.subscribers.map(s => (
                            <tr key={s.id}>
                              <td className="font-bold">{s.name}</td>
                              <td>{s.email}</td>
                              <td className="text-black/60 text-sm">{formatDate(s.created_at)}</td>
                              <td><button onClick={() => handleDeleteSub(s.id)} className="p-2 text-black/60 hover:text-red-500"><Trash2 size={16} /></button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* CAR MODAL */}
      {showCarModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white w-full max-w-2xl my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-black/10 sticky top-0 bg-white z-10">
              <h3 className="text-xl font-black uppercase">{editingCar ? "Edit Vehicle" : "Add Vehicle"}</h3>
              <button onClick={() => setShowCarModal(false)}><X size={20} /></button>
            </div>

            <form onSubmit={handleSaveCar} className="p-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="block text-sm font-bold mb-1">Make *</label><input type="text" name="make" value={carForm.make} onChange={handleCarChange} required className="w-full border border-black/20 p-3 focus:border-[#E31E24] focus:outline-none" /></div>
                <div><label className="block text-sm font-bold mb-1">Model *</label><input type="text" name="model" value={carForm.model} onChange={handleCarChange} required className="w-full border border-black/20 p-3 focus:border-[#E31E24] focus:outline-none" /></div>
                <div><label className="block text-sm font-bold mb-1">Year *</label><input type="number" name="year" value={carForm.year} onChange={handleCarChange} required min="1990" max="2027" className="w-full border border-black/20 p-3 focus:border-[#E31E24] focus:outline-none" /></div>
                <div><label className="block text-sm font-bold mb-1">Trim</label><input type="text" name="trim" value={carForm.trim} onChange={handleCarChange} className="w-full border border-black/20 p-3 focus:border-[#E31E24] focus:outline-none" placeholder="e.g., SXT, LS" /></div>
                <div><label className="block text-sm font-bold mb-1">Price *</label><input type="number" name="price" value={carForm.price} onChange={handleCarChange} required min="0" className="w-full border border-black/20 p-3 focus:border-[#E31E24] focus:outline-none" /></div>
                <div><label className="block text-sm font-bold mb-1">Mileage *</label><input type="number" name="mileage" value={carForm.mileage} onChange={handleCarChange} required min="0" className="w-full border border-black/20 p-3 focus:border-[#E31E24] focus:outline-none" /></div>
                <div><label className="block text-sm font-bold mb-1">Status</label><select name="status" value={carForm.status} onChange={handleCarChange} className="w-full border border-black/20 p-3 bg-white"><option value="available">Available</option><option value="pending">Pending</option><option value="sold">Sold</option></select></div>
                <div><label className="block text-sm font-bold mb-1">VIN (Optional)</label><input type="text" name="vin" value={carForm.vin} onChange={handleCarChange} className="w-full border border-black/20 p-3 focus:border-[#E31E24] focus:outline-none" /></div>
              </div>

              <div><label className="block text-sm font-bold mb-1">Features</label><textarea name="features" value={carForm.features} onChange={handleCarChange} rows={2} className="w-full border border-black/20 p-3 focus:border-[#E31E24] focus:outline-none resize-none" placeholder="e.g., 3rd row seating, AC, Automatic..." /></div>
              <div><label className="block text-sm font-bold mb-1">Description</label><textarea name="description" value={carForm.description} onChange={handleCarChange} rows={3} className="w-full border border-black/20 p-3 focus:border-[#E31E24] focus:outline-none resize-none" /></div>

              <label className="flex items-center gap-2"><input type="checkbox" name="featured" checked={carForm.featured} onChange={handleCarChange} className="w-5 h-5 accent-[#E31E24]" /><span className="font-bold text-sm">Feature on Homepage</span></label>

              <div>
                <label className="block text-sm font-bold mb-2">Vehicle Photos</label>
                <div className="flex gap-2 mb-3">
                  <input type="url" value={newImageUrl} onChange={(e) => setNewImageUrl(e.target.value)} placeholder="Paste image URL" className="flex-1 border border-black/20 p-3 focus:border-[#E31E24] focus:outline-none text-sm" />
                  <button type="button" onClick={addImageToCar} className="bg-black text-white font-bold uppercase px-4 py-3 hover:bg-black/80 text-sm">Add</button>
                </div>
                {carForm.images.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                    {carForm.images.map((img, idx) => (
                      <div key={idx} className="relative group">
                        <img src={img} alt="" className="w-full aspect-square object-cover border border-black/10" />
                        {idx === 0 && <span className="absolute top-1 left-1 bg-[#E31E24] text-white text-xs px-1 font-bold">MAIN</span>}
                        <button type="button" onClick={() => removeImageFromCar(idx)} className="absolute top-1 right-1 bg-red-500 text-white p-1 opacity-0 group-hover:opacity-100"><X size={12} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-4 border-t border-black/10">
                <button type="button" onClick={() => setShowCarModal(false)} className="flex-1 border border-black/20 font-bold uppercase py-3 hover:bg-black hover:text-white">Cancel</button>
                <button type="submit" className="flex-1 bg-[#E31E24] text-white font-bold uppercase py-3 hover:bg-[#C01A1F] flex items-center justify-center gap-2"><Save size={18} />{editingCar ? "Update" : "Add"}</button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* SOLD MODAL */}
      {showSoldModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md">
            <div className="flex justify-between p-6 border-b border-black/10">
              <h3 className="text-xl font-black uppercase">Add Sold Photo</h3>
              <button onClick={() => setShowSoldModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handleAddSold} className="p-6">
              <label className="block text-sm font-bold mb-2">Image URL</label>
              <input type="url" value={newSoldUrl} onChange={(e) => setNewSoldUrl(e.target.value)} required
                placeholder="https://..." className="w-full border border-black/20 p-3 focus:border-[#E31E24] focus:outline-none mb-4" />
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowSoldModal(false)} className="flex-1 border border-black/20 font-bold uppercase py-3 hover:bg-black hover:text-white">Cancel</button>
                <button type="submit" className="flex-1 bg-[#E31E24] text-white font-bold uppercase py-3 hover:bg-[#C01A1F]">Add</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* TRANSPORT MANAGE MODAL */}
      {showTransportModal && selectedTransport && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white w-full max-w-2xl my-8">
            <div className="flex justify-between items-center p-6 border-b border-black/10">
              <h3 className="text-xl font-black uppercase">Manage Transport Request</h3>
              <button onClick={() => setShowTransportModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handleUpdateTransport} className="p-6 space-y-4">
              <div className="bg-gray-50 p-4 border border-black/10">
                <p className="font-bold">{selectedTransport.full_name}</p>
                <p className="text-sm text-black/60">{selectedTransport.vehicle_year} {selectedTransport.vehicle_make} {selectedTransport.vehicle_model}</p>
              </div>

              <div>
                <label className="block text-sm font-bold mb-2">Status</label>
                <select value={transportUpdate.status} onChange={(e) => setTransportUpdate({...transportUpdate, status: e.target.value})}
                  className="w-full border border-black/20 p-3 focus:border-[#E31E24] focus:outline-none bg-white">
                  <option value="new">New</option>
                  <option value="quoted">Quoted</option>
                  <option value="awaiting_approval">Awaiting Approval</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold mb-2">Quote Amount ($)</label>
                <input type="number" step="0.01" value={transportUpdate.quote_amount} onChange={(e) => setTransportUpdate({...transportUpdate, quote_amount: e.target.value})}
                  placeholder="0.00" className="w-full border border-black/20 p-3 focus:border-[#E31E24] focus:outline-none" />
              </div>

              <div>
                <label className="block text-sm font-bold mb-2">Admin Notes</label>
                <textarea value={transportUpdate.admin_notes} onChange={(e) => setTransportUpdate({...transportUpdate, admin_notes: e.target.value})}
                  rows={4} placeholder="Internal notes..." className="w-full border border-black/20 p-3 focus:border-[#E31E24] focus:outline-none resize-none" />
              </div>

              <div className="flex gap-3 pt-4 border-t border-black/10">
                <button type="button" onClick={() => setShowTransportModal(false)} className="flex-1 border border-black/20 font-bold uppercase py-3 hover:bg-black hover:text-white">Cancel</button>
                <button type="submit" className="flex-1 bg-[#E31E24] text-white font-bold uppercase py-3 hover:bg-[#C01A1F]">Update</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
